<template lang="pug">
.products-side-bar
  section.promotions(v-if="showPromotionFilter")
    .title Promotions
    el-checkbox-group.list(
      v-model="filters['q[promo_modes]']"
      @change="() => debounced()")
      el-checkbox.pf-block.filter-promo.buy-in(
        :label="PROMOTION_SHORT_TYPES.BUY_IN"
        @change="$amplitude.track('CLICK_PROMOTION_TYPES')")
        .label-text
          span BUY-IN
          promotion-tag.uppercase.ml-2(type="buy_in" :showPopover="false")
      el-checkbox.pf-block.filter-promo.tpr(
        :label="PROMOTION_SHORT_TYPES.TPR"
        @change="$amplitude.track('CLICK_PROMOTION_TYPES')")
        .label-text
          span TEMPORARY PRICE REDUCTION
          promotion-tag.uppercase.ml-2(type="tpr" :showPopover="false")

  section.express(v-if="showExpressFilter || showGrowFilter || showInStockFilter")
    .title Conditions
    .list
      el-checkbox.pf-block.filter-delivery.express-only(
        v-if="showExpressFilter"
        size="small"
        true-label="false"
        v-model="filters['q[pfd]']"
        @change="() => {debounced(); $amplitude.track('CLICK_EXPRESS_ONLY')}")
        .label-text
          span EXPRESS ONLY
          express-tag.ml-2
      el-checkbox.pf-block.filter-delivery.grow-only(
        v-else-if="showGrowFilter"
        size="small"
        v-model="filters['q[pfg]']"
        @change="() => {debounced(); $amplitude.track('CLICK_GROW_ONLY')}")
        div.label-text
          span GROW ONLY
          grow-tag.ml-2
      el-checkbox.pf-block.filter-delivery.in-stock-only(
        v-if="showInStockFilter"
        size="small"
        true-label="in_stock"
        v-model="filters['q[product_availability]']"
        @change="() => {debounced(); $amplitude.track('CLICK_IN_STOCK_ONLY')}")
        div.label-text
          span IN STOCK
          promotion-tag.uppercase.ml-2(type="short_dated" text="in stock" :showPopover="false")

  section.brand-locations
    .title
      help-tag(tooltip="Filter products by state") State (Province/Territory)
    address-state-select(
      v-model="filters['q[address_state_id]']"
      size="small"
      filterable
      clearable
      placeholder="Select brand location"
      @change="() => {debounced(); $amplitude.track('CHANGE_PRODUCT_PROVINCE')}")

  section.sub-categories(
    v-if="category && category.product_types && category.product_types.length")
    .title(v-text="category.name")
    .content
      el-radio-group.list(
        v-model="filters['q[product_type_id]']"
        @change="() => debounced()")
        el-radio(
          v-for="i, k in category.product_types"
          :key="k"
          :title="i.name"
          :label="i.id"
          @change="$amplitude.track('CLICK_CATEGORY_ITEM', { category_id: i.id })") {{ i.name }}

  section.product-tags(v-if="productTags.length")
    .title Tag
    .content
      el-checkbox-group.list(
        v-model="filters['q[tag_ids]']"
        @change="() => debounced()")
        el-checkbox(
          v-for="i, k in productTags"
          :key="k"
          :title="i.name"
          :label="i.id"
          @change="$amplitude.track('CLICK_TAG', { tag_id: i.id })") {{ i.name }}

  section.product-qualities(v-if="productQualities.length")
    .title Product Quality
    .content
      el-checkbox-group.list(
        v-model="filters['q[product_quality]']"
        @change="() => debounced()")
        el-checkbox(
          v-for="i, k in productQualities"
          :key="k"
          :title="i.name"
          :label="i.id"
          @change="$amplitude.track('CLICK_PRODUCT_QUALITY', { product_quality_id: i.id, product_quality_name: i.name })") {{ i.name }}

  section.package-sizes(v-if="packageSizes.length")
    .title Package Size
    .content
      el-checkbox-group.list(
        v-model="filters['q[package_size]']"
        @change="() => debounced()")
        el-checkbox(
          v-for="i, k in packageSizes"
          :key="k"
          :label="i.id"
          @change="$amplitude.track('CLICK_PACKAGE_SIZE', { package_size_id: i.id })") {{ i.name }}
</template>

<script>
import { mapGetters } from 'vuex'
import { debounce } from 'lodash'
import { DEBOUNCE_DELAY, PROMOTION_SHORT_TYPES } from '~/settings'
import { getDefaultQuery, serializeQuery, deserializeQuery } from '~/utilities/catalog'
import PromotionTag from '~/components/tags/PromotionTag'
import GrowTag from '~/components/tags/GrowTag'

export default {
  components: {
    PromotionTag,
    GrowTag
  },

  data () {
    return {
      PROMOTION_SHORT_TYPES,
      filters: getDefaultQuery(),
      debounced: null
    }
  },

  computed: {
    ...mapGetters({
      hidePodGrowFilter: 'configs/hidePodGrowFilter',
      isBuyer: 'authentication/isBuyer',
      isHeadBuyer: 'authentication/isHeadBuyer',
      headBuyerHasAssignedRegions: 'authentication/headBuyerHasAssignedRegions',
      isWarehouseHeadBuyer: 'authentication/isWarehouseHeadBuyer',
      isPodDirectHeadBuyer: 'authentication/isPodDirectHeadBuyer',
      isWarehouseBuyer: 'authentication/isWarehouseBuyer',
      isPodDirectBuyer: 'authentication/isPodDirectBuyer',
      isPreApprovalBuyer: 'authentication/isPreApprovalBuyer',
      productQualities: 'master/productQualities',
      productTags: 'master/productTags',
      packageSizes: 'master/packageSizes'
    }),

    category () {
      const id = this.filters.category_id
      if (!id) return null
      if (id === 0) return {
        id: 0,
        name: 'New'
      }
      return this.$store.getters['master/productCategoryById'](id)
    },

    showPromotionFilter () {
      return this.isBuyer || this.isHeadBuyer
    },

    showExpressFilter () {
      return (this.isWarehouseBuyer && !this.isPreApprovalBuyer) || (this.headBuyerHasAssignedRegions && this.isWarehouseHeadBuyer)
    },

    showGrowFilter () {
      if (this.hidePodGrowFilter) return false
      return (this.isPodDirectBuyer && !this.isPreApprovalBuyer) || (this.headBuyerHasAssignedRegions && this.isPodDirectHeadBuyer)
    },

    showInStockFilter () {
      return this.isBuyer || this.isHeadBuyer
    }
  },

  watch: {
    '$route.query': {
      immediate: true,
      handler (query) {
        this.filters = deserializeQuery(query)
      }
    }
  },

  mounted () {
    this.debounced = debounce(this.filter, DEBOUNCE_DELAY)
  },

  methods: {
    filter () {
      const route = {
        query: serializeQuery(this.filters)
      }
      this.$router.push(route)?.catch(this.$debug.log)
    }
  }
}
</script>

<style lang="scss" scoped>
.uppercase {
  text-transform: uppercase;
}
.products-side-bar {
  padding: 2em 0;
  section {
    &:not(:last-child) {
      margin-bottom: 30px;
    }
    .title {
      font-size: 13.5px;
      font-weight: $--font-weight-bold;
      margin-bottom: 1em;
    }
    .list {
      display: grid;
      grid-template-columns: 1fr;
      grid-gap: 4px;
      max-height: 250px;
      overflow-y: auto;
      @include modern-scroller();
      &::-webkit-scrollbar-thumb {
        border: none;
      }
      ::v-deep {
        .el-checkbox , .el-radio {
          overflow: hidden;
          &__label {
            font-size: 13.5px;
            line-height: 20px;
            color: $--color-text-secondary;
            white-space: pre-wrap;
            display: inline-block;
          }
        }
        .el-radio {
          display: flex;
          align-items: center;
          .is-checked {
            .el-radio__inner {
              &::after {
                transform: rotate(45deg) scaleY(1);
                box-sizing: content-box;
                content: "";
                border: 1px solid #fff;
                border-left: 0;
                border-top: 0;
                border-radius: 0;
                background: inherit;
                height: 7px;
                left: 4px;
                position: absolute;
                top: 1px;
                width: 3px;
                transition: transform .15s ease-in .05s;
                transform-origin: center;
              }
            }
          }
          .el-radio__inner {
            border-radius: 2px;
          }
        }
      }
    }
  }
  .filter-promo,
  .filter-delivery {
    height: 40px;
    display: flex;
    align-items: center;
    padding: 4px 0;
    border-radius: 8px;
    background-repeat: no-repeat;
    background-position: center right 0px;
    ::v-deep {
      .el-checkbox__label {
        .label-text {
          padding-left: 1px;
          font-size: 12.5px;
          line-height: 14px;
          display: flex;
          align-items: center;
        }
      }
    }
    &.buy-in {
      ::v-deep {
        .el-checkbox__input {
          height: auto;
          &.is-checked {
            .el-checkbox__inner { border-color: white; }
          }
        }
      }
    }
    &.tpr {
      ::v-deep {
        .el-checkbox__input {
          height: auto;
          &.is-checked {
            .el-checkbox__inner {
              border-color: white;
              background-color: $--color-success;
            }
          }
        }
      }
    }
    &.express-only {
      ::v-deep {
        .el-checkbox__input {
          height: auto;
          &.is-checked {
            .el-checkbox__inner {
              border-color: white;
              background-color: #00B3ED;
            }
          }
        }
      }
    }
    &.in-stock-only {
      ::v-deep {
        .el-checkbox__input {
          height: auto;
          &.is-checked {
            .el-checkbox__inner {
              border-color: white;
              background-color: #fbb122;
            }
          }
        }
      }
    }
    &.grow-only {
      ::v-deep {
        .el-checkbox__input {
          height: auto;
          &.is-checked {
            .el-checkbox__inner {
              border-color: white;
              background-color: #7B61FF;
            }
          }
        }
      }
    }
  }
}
</style>
